import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
  forwardRef,
} from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import * as d3 from "d3";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import { useDimensions } from "common/hooks";
import {
  expressTarget,
  retrieveRange,
  doSystemDateRetrieval,
  parseSystemDate,
  calcRangeExtent,
  isInsideRange,
} from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import NVLineChart from "common/graphs/NVLineChart";
import formats from "common/graphs/formats";
import ActionsContext from "common/ActionsContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ScatterPlot from "common/graphs/ScatterPlot/ScatterPlot";
import { TileContainer } from "common/graphs/FacetGraph";
import FacetGraph from "common/graphs/ConnectedFacetGraph";
import {
  parseSubscriberFlows,
  calcFlowsCreationPerMinute,
  parseSubscriberMaxSpeeds,
  parseSubscriberLatencies,
  parseSubscriberRetransmissions,
  parseTrafficCongestion,
  parseLatencyReduction,
  parseRetransmissionsReduction,
} from "./api";
import { toObjectLiteral } from "common/utils";

const Wrapper = styled.div`
  .historical-metrics-content{
    .hasExtraBoldLine {
      .graph .nvd3-svg{
        g.nv-groups {
          g.nv-series-0 {
          stroke-width: 4 !important;
          path {
          stroke-width: 4 !important;
          }
        }
      }
    } 
  }
}
`;

const EVENT_HIGHLIGHT = "nv-line-chart-highlight";

const ControlsContainer = styled.div`
  width: 100%;
  display: flex;
  padding: 0 0 20px 0;
  justify-content: space-between;
  align-items: center;
`;

const SubcriberDetailsContextControlsLayout = styled.div`
  display: flex;
  flex-layout: row;
  width: 100%;
  *:first-child {
    flex: 10% 1 0;
  }
  *:last-child {
    flex: 100% 1 1;
  }
  & .input-group {
    max-width: 20rem;
  }
`;

const ContextMenuDiv = styled.div`
  display: flex;
  align-items: center;

  i {
    color: #999;
    font-size: 20px;

    &:hover {
      color: #000;
    }
  }
`;

const H4Centered = styled.h4`
  font-size: 18px;
  margin-top: 7px;
  margin-bottom: 7px;
`;

const doesNothing = () => {};
const MARGIN_PLOT = { top: 0, right: 40, bottom: 50, left: 60 };
const PADDING_CHART_CONTAINER = 30;

const noIPv6Mask = { ipv6Mask: false };

export const doStatsRetrieval = ({
  target = null,
  facet = "volume",
  hours = 24,
  interval = 10,
}) =>
  expressTarget(target, noIPv6Mask).then((expressedTarget) =>
    ifCl.run(
      `show statistics subscribers ${facet} ${expressedTarget} hours ${hours} interval ${interval}`
    )
  );

const systemTimeRetrieval = () => doSystemDateRetrieval().then(parseSystemDate);

const _hoursScopeChoices = [
  { value: 24, label: "1 Day" },
  { value: 24 * 7, label: "1 Week" },
  { value: 24 * 31, label: "1 Month" },
  { value: 24 * 31 * 3, label: "3 Months" },
];

const SubcriberDetailsContextControls = ({
  range,
  hours,
  onChange = doesNothing,
  children,
}) => {
  const handleSelectChange = ({ target }) => {
    onChange({ hours: parseInt(target.value) });
  };

  return (
    <>
      <TileContainer className="full-width can-compress no-print historical-metrics-controls">
        <SelectInputWithIcon
          title="Date Range"
          name="Date Range"
          icon="date_range"
          selected={hours || 24}
          onChange={handleSelectChange}
          options={_hoursScopeChoices}
        />
      </TileContainer>
      {children}
      <TileContainer className="full-width can-compress no-print">
        <RangeSliderInput {...range} onChange={onChange} />
      </TileContainer>
    </>
  );
};

const parseAndCalcFlowsCreationPerMinute = (response) =>
  calcFlowsCreationPerMinute(parseSubscriberFlows(response));

const scatterItemsXSpeed = (yItems, speedItems, yLabel) =>
  yItems && yItems.length > 0 ?
  speedItems.reduce((acc, item, index) => {
    const { downMbPerSec, time } = item;
    if (yItems[index][yLabel] !== null && downMbPerSec !== null)
      acc.push({ y: yItems[index][yLabel], x: downMbPerSec, time });
    return acc;
  }, [])
  : [];

const formatDate = d3.timeFormat("%m/%d %H:%M");

function getTimeValuesMaps(itemsCharts) {
  return Object.keys(itemsCharts).reduce((acc, itemsChart) => {
    const mapKey = `${itemsChart}Map`;
    const items = itemsCharts[itemsChart];
    if (items && items.length === 0) {
      acc[mapKey] = {};
    }
    const mapValues = items.reduce((acc, value) => {
      const { time } = value;
      const timeKey = formatDate(time);
      acc[timeKey] = value;
      return acc;
    }, {});
    acc[mapKey] = mapValues;
    return acc;
  }, {});
}

const ScatterPlotLatencySpeed = forwardRef(
  ({ items, interactionData, onInteractionData }, ref) => {
    return (
      <TileContainer className="chart-container" ref={ref}>
        <h4 className="chart-title lowercase">Latency vs Average Speed</h4>
        <div className="graph">
          <ScatterPlot
            items={items}
            contentY={{
              yAxisLabel: "Access latency (ms)",
              tooltipLabel: "Latency",
              tooltipUnit: "ms",
            }}
            contentX={{
              xAxisLabel: "Average downlink speed (Mbps)",
              tooltipLabel: "Average Speed",
              tooltipUnit: "Mbps",
            }}
            interactionData={interactionData}
            onInteractionData={onInteractionData}
          />
        </div>
      </TileContainer>
    );
  }
);

ScatterPlotLatencySpeed.displayName = 'ScatterPlotLatencySpeed'; 

const SubscriberAnalyticsResult = ({
  target,
  range: initialRange,
  itemsSpeed,
  itemsMaxSpeed,
  itemsLatency,
  itemsRetransmission,
  itemsFlows,
  itemsCongestionLatency,
  itemsCongestionTraffic,
  groups,
  interval = 10,
  hours = 24,
  onHoursChange = doesNothing,
}) => {
  const scatterPlotRef = useRef();
  const dimensions = useDimensions(scatterPlotRef);
  const widthChart =
    dimensions.width -
    MARGIN_PLOT.left -
    MARGIN_PLOT.right -
    PADDING_CHART_CONTAINER;
  const [range, setRange] = useState(initialRange);
  const applyRange = (newRange) => setRange(newRange);
  const [interactionData, setInteractionData] = useState(null);
  const actions = useContext(ActionsContext);
  const applyChange = ({ hours, ...range }) => {
    if (hours !== undefined) {
      onHoursChange(hours);
    } else {
      applyRange(range);
    }
  };

  const {
    itemsSpeedMap,
    itemsLatencyMap,
    itemsFlowsMap,
    itemsRetransmissionMap,
    itemsMaxSpeedMap,
    itemsCongestionLatencyMap,
    itemsCongestionTrafficMap,
  } = useMemo(() => {
    return getTimeValuesMaps({
      itemsSpeed,
      itemsLatency,
      itemsFlows,
      itemsRetransmission,
      itemsMaxSpeed,
      itemsCongestionLatency,
      itemsCongestionTraffic,
    });
  }, [
    itemsSpeed,
    itemsLatency,
    itemsFlows,
    itemsRetransmission,
    itemsMaxSpeed,
    itemsCongestionLatency,
    itemsCongestionTraffic,
  ]);

  const xScale = useMemo(() => {
    return d3
      .scaleTime()
      .range([0, widthChart])
      .domain([itemsSpeed[0].time, itemsSpeed[itemsSpeed.length - 1].time]);
  }, [itemsSpeed, widthChart]);

  return (
    <Wrapper>
    <ColumnsGrid
      gap="1rem"
      columns={2}
      minWidth="9cm"
      className="historical-metrics-content"
    >
      <SubcriberDetailsContextControls
        target={target}
        range={initialRange}
        hours={hours}
        onChange={applyChange}
      >
        <FacetGraph
          title="Average Speed Over Time"
          items={itemsSpeed.filter(isInsideRange(range))}
          facet="speed"
          yAxisUnits="Mbps"
          fields={[
            { name: "downMbPerSec", label: "Down-Mbps" },
            { name: "upMbPerSec", label: "Up-Mbps" },
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsSpeedMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <FacetGraph
          title="Maximum Speed Over Time"
          items={itemsMaxSpeed.filter(isInsideRange(range))}
          facet="max-speed"
          yAxisUnits="Mbps"
          fields={[
            { name: "downMbPerSec", label: "Down-Mbps" },
            { name: "upMbPerSec", label: "Up-Mbps" },
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsMaxSpeedMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <FacetGraph
          title="Latency Over Time"
          items={itemsLatency.filter(isInsideRange(range))}
          facet="latency"
          yAxisUnits="milliseconds"
          fields={[
            { name: "SUBS-ACCESS-RTT-MS", label: "Subs-Access-Rtt-Ms" },
            ...groups.map((group) => {
              if(group==='all-subscribers'){
                return {
                  name: "all-subscribers",
                  label: "All Subscribers"
                }
              }

              return {
                name: group,
                label: group,
              };
            })
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsLatencyMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
          addSerieNameToClass={true}
          className="hasExtraBoldLine"
        />
        <FacetGraph
          title="Packet retransmissions Over Time"
          items={itemsRetransmission.filter(isInsideRange(range))}
          facet="retransmission"
          yAxisUnits="% retransmisions"
          fields={[
            { name: "subsAccessRtx", label: "Subs-Access-Rtx-%" },
            ...groups.map((group) => {
              if(group==='all-subscribers'){
                return {
                  name: "all-subscribers",
                  label: "All Subscribers"
                }
              }

              return {
                name: group,
                label: group,
              };
            })
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsRetransmissionMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
          addSerieNameToClass={true}
          className="hasExtraBoldLine"
        />
        <FacetGraph
          title="Average traffic at high speed and under congestion Over Time"
          items={itemsCongestionTraffic.filter(isInsideRange(range))}
          facet="congestion-total"
          yAxisUnits="% of total traffic"
          fields={[
            { name: "maxSpeed", label: "Max-speed-traffic-%" },
            { name: "congestion", label: "Congested-traffic-%" },
            { name: "acm", label: "ACM-%" },
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsCongestionTrafficMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <FacetGraph
          title="Flows Over Time"
          items={itemsFlows.filter(isInsideRange(range))}
          facet="flows"
          yAxisUnits="Flows"
          fields={[
            { name: "flCreatedPerMinute", label: "Fl-Created-per-minute" },
            { name: "flActive", label: "Fl-Active" },
          ]}
          onInteractionData={setInteractionData}
          interactionData={interactionData}
          valuesMap={itemsFlowsMap}
          xScale={xScale}
          widthChart={widthChart}
          hours={hours}
        />
        <ScatterPlotLatencySpeed
          items={scatterItemsXSpeed(
            itemsLatency,
            itemsSpeed,
            "SUBS-ACCESS-RTT-MS"
          ).filter(isInsideRange(range))}
          interactionData={interactionData}
          onInteractionData={setInteractionData}
          ref={scatterPlotRef}
        />
        <TileContainer className="chart-container">
          <h4 className="chart-title lowercase">
            Packet Retransmissions vs Average Speed
          </h4>
          <div className="graph">
            <ScatterPlot
              items={scatterItemsXSpeed(
                itemsRetransmission,
                itemsSpeed,
                "subsAccessRtx"
              ).filter(isInsideRange(range))}
              contentY={{
                yAxisLabel: "Retransmissions (%)",
                tooltipLabel: "Retransmissions",
                tooltipUnit: "%",
              }}
              contentX={{
                xAxisLabel: "Average downlink speed (Mbps)",
                tooltipLabel: "Average Speed",
                tooltipUnit: "Mbps",
              }}
              interactionData={interactionData}
              onInteractionData={setInteractionData}
            />
          </div>
        </TileContainer>
        <TileContainer className="chart-container">
          <h4 className="chart-title lowercase">Congestion vs Average Speed</h4>
          <div className="graph">
            <ScatterPlot
              items={scatterItemsXSpeed(
                itemsCongestionTraffic,
                itemsSpeed,
                "congestion"
              ).filter(isInsideRange(range))}
              contentY={{
                yAxisLabel: "Congested traffic (%)",
                tooltipLabel: "Congestion",
                tooltipUnit: "%",
              }}
              contentX={{
                xAxisLabel: "Average downlink speed (Mbps)",
                tooltipLabel: "Average Speed",
                tooltipUnit: "Mbps",
              }}
              interactionData={interactionData}
              onInteractionData={setInteractionData}
            />
          </div>
        </TileContainer>
      </SubcriberDetailsContextControls>
    </ColumnsGrid>
    </Wrapper>
  );
};

const promisesInfo = [
  { facet: "speed", parse: parseSubscriberMaxSpeeds },
  { facet: "max-speed", parse: parseSubscriberMaxSpeeds },
  { facet: "latency", parse: parseSubscriberLatencies },
  { facet: "retransmission", parse: parseSubscriberRetransmissions },
  { facet: "flows", parse: parseAndCalcFlowsCreationPerMinute },
  { facet: "congestion", parse: parseLatencyReduction },
  { facet: "congestion", parse: parseTrafficCongestion },
];

const promises = ({ target, hours, interval, ipAddressSelected }) => {
  const promisesStats =  promisesInfo.map(({ facet, parse }) => {
    return doStatsRetrieval({
      facet,
      target,
      hours,
      interval,
    })
      .then(parse)
      .catch((error) => console.error(error) || []);
  })
  return Promise.all([...promisesStats, fetchSubscriberGroupsItems(target, hours, interval, ipAddressSelected)])
};

const promisesGroupsKeys = ["latency-down", "retransmission-down"];

const parseGroupsResponse=(response)=> {
  const rows = response.trim().split("\n");
  const subGroupRows = rows.filter((row) => row.includes('subscriber-groups'));
  const subGroups = subGroupRows.reduce((acc, row) => {
    const [prefix, groupsResult] = row.split(': ');
    const groups = groupsResult.split(' ').reduce((accGroups, group) => {
      const groupParsed = group.replace(/"/g, '');
      if (groupParsed && groupParsed !== 'n/a') {
        accGroups.push(groupParsed);
      }
      return accGroups;
    }, []);
    return [...groups, ...acc];
  }, []);
  const groupsSet = new Set(subGroups);
  return groupsSet;
}

const getPromiseGroups = (target, ipAddressSelected) => {
  const { addr, subsId } = target;
  if (addr) {
    return ifCl.run(`show api subscriber ${addr}`).then((response) => {
      const groupsSet = parseGroupsResponse(response);
      return [...groupsSet];
    });
  }
  return ifCl
    .run(`show subscriber id ${subsId} short`)
    .then((response) => {
      const blocks = response.trim().split("\n\n");
      const ipAddresses = blocks.reduce((acc, block) => {
        const rows = block.trim().split("\n");
        const [label, ipAddressRaw] = rows[0].split(":");
        const ipAddress = ipAddressRaw.trim();
        acc.push(ipAddress);
        return acc;
      }, []);
      const ipPromises = [ipAddressSelected].map((ip) => {
        return ifCl.run(`show api subscriber ${ip}`).then((response) => {
          const groupsSet = parseGroupsResponse(response);
          return [...groupsSet];
        });
      });
      return Promise.all(ipPromises);
    })
    .then((responses) => {
      const groupsTotal = responses.flat();
      const groupsSet = new Set(groupsTotal);
      return [...groupsSet];
    });
};

const fetchSubscriberGroupsItems = (target, hours, interval, ipAddressSelected) => {
  return getPromiseGroups(target, ipAddressSelected).then((groups) => {
    if (!groups || groups.length === 0) {
      return { groups: [], itemsLatency: [], itemsRetransmissions: [] };
    }

    const csvGroups = groups
      .map((group) => `"${group.replaceAll('"', '""')}"`)
      .join(",");
    const promisesGroups = promisesGroupsKeys.map((key) => {
      return ifCl.run(
        `show statistics subscriber-groups name '${csvGroups}' ${key} hours ${hours} interval ${interval}`
      );
    });

    return Promise.all(promisesGroups).then(
      ([responseLatency, responseRetransmissions]) => {
        return {
          groups: groups,
          itemsLatency: parseSubscriberLatencies(responseLatency),
          itemsRetransmissions: parseSubscriberRetransmissions(
            responseRetransmissions
          ),
        };
      }
    );
  });
};

const timePeriodMap = {
  24: "oneDay",
  168: "oneWeek",
  744: "oneMonth",
  2232: "threeMonths",
};

const retrieval = ({ target, hours, interval, ipAddressSelected }) => {
  return Promise.all([
    systemTimeRetrieval().then((systemDate) =>
      calcRangeExtent(systemDate, hours)
    ),
    promises({ target, hours, interval, ipAddressSelected }),
  ]).then(
    ([
      range,
      [
        itemsSpeed,
        itemsMaxSpeed,
        itemsLatency,
        itemsRetransmission,
        itemsFlows,
        itemsCongestionLatency,
        itemsCongestionTraffic,
        responseSubscriberGroups
      ],     
    ]) => {
      const {itemsLatency:itemsLatencyGroups, groups:latencyGroups, itemsRetransmissions: itemsRetransmissionsGroups} = responseSubscriberGroups;
      const itemsLatencyTotal = itemsLatency.map((item, index)=>{
          return {...item, ...itemsLatencyGroups[index]}
      })
      const itemsRetransmissionTotal = itemsRetransmission.map((item, index)=>{
        return {...item, ...itemsRetransmissionsGroups[index]}
      })
      return {
        range,
        itemsSpeed,
        itemsMaxSpeed,
        itemsLatency: itemsLatencyTotal,
        itemsRetransmission: itemsRetransmissionTotal,
        itemsFlows,
        itemsCongestionLatency,
        itemsCongestionTraffic,
        groups: latencyGroups,
      };
    }
  )
};

const SubscriberAnalyticContextRequest = ({
  target,
  context,
  onHoursChange = doesNothing,
  ipAddressSelected,
}) => {
  const [request, setRequest] = useState(null);
  let [searchParams, setSearchParams] = useSearchParams();

  const doLoad = () => {
    setRequest(context.hours ? retrieval({ target, ...context, ipAddressSelected }) : null);
  };

  const targetStr = target ? (target.addr ? target.addr : target.subsId) : "";
  const contextStr = "" + context.hours + context.interval;

  useEffect(() => {
    if (targetStr) {
      doLoad();
    }
  }, [contextStr, targetStr, ipAddressSelected]);

  return request === null ? null : (
    <Request during={request}>
      {(result) => {
        const { range } = result;
        return (
          <>
            <ControlsContainer className="historical-metrics-header">
              <H4Centered className="subtitle"> Historical Metrics </H4Centered>
              <ContextMenuDiv className="context-menu">
                <a
                  onClick={doLoad}
                  data-toggle="cardloading"
                  data-loading-effect="pulse"
                  title="Refresh"
                >
                  <i className="material-icons">refresh</i>
                </a>
              </ContextMenuDiv>
            </ControlsContainer>
            <SubscriberAnalyticsResult
              {...result}
              {...context}
              target={target}
              onHoursChange={(hours) => {
                onHoursChange(hours);
                setSearchParams({
                  ...toObjectLiteral(searchParams),
                  timePeriod: timePeriodMap[hours],
                });
              }}
            />
          </>
        );
      }}
    </Request>
  );
};

const _defaultContext = {
  hours: 24,
  interval: 10,
};

const intervalForHours = (hours, interval = 10) =>
  hours > 7 * 24 ? 60 : interval;

const hoursMap = {
  oneDay: 24,
  oneWeek: 24 * 7,
  oneMonth: 24 * 31,
  threeMonths: 24 * 31 * 3,
};

const SubscriberAnalyticContext = ({ target, ipAddressSelected }) => {
  let [searchParams, _] = useSearchParams();

  const [context, setContext] = useState({
    ..._defaultContext,
    hours: hoursMap[searchParams.get("timePeriod")] ?? _defaultContext.hours,
  });

  const setHoursInContext = (hours) =>
    setContext({ hours, interval: intervalForHours(hours) });

  return (
    <SubscriberAnalyticContextRequest
      target={target}
      context={context}
      onHoursChange={setHoursInContext}
      ipAddressSelected={ipAddressSelected}
    />
  );
};
export default SubscriberAnalyticContext;


